import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './styles/ItineraryComparison.style';
import { ItinerarySegmentViewModel } from '../../services/api/flightChanges/types';

export interface ItinerarySegmentProps {
    segment: ItinerarySegmentViewModel;
    variant: 'previous' | 'new';
    isFirst?: boolean;
    isLast?: boolean;
}

const ItinerarySegmentComponent: React.FC<ItinerarySegmentProps> = ({
    segment,
    variant,
    isFirst = false,
    isLast = false
}) => {
    const { t } = useTranslation();

    return (
        <S.FlightSegment 
            variant={variant}
            $isFirst={isFirst}
            $isLast={isLast}
            role="listitem"
            aria-label={`${variant === 'previous' ? t('itinerary.previous') : t('itinerary.new')} flight ${segment.flightNumber}${segment.flown ? ` (${t('itinerary.flown')})` : ''} from ${segment.originAirport} to ${segment.destinationAirport}`}
        >
            <S.FlightNumber variant={variant}>
                {segment.flightNumber}
                {segment.flown === true && (
                    <S.FlownStatus> ({t('itinerary.flown')})</S.FlownStatus>
                )}
            </S.FlightNumber>
            <S.FlightDetailsContainer>
                <S.FlightInfo>
                    <S.FlightMeta>
                        -
                    </S.FlightMeta>
                    <S.FlightMeta>
                        -
                    </S.FlightMeta>
                </S.FlightInfo>
                <S.FlightTimes>
                    <S.FlightDateTime>
                        <S.FlightDate variant={variant}>
                            {segment.etd.isValid ? segment.etd.date : '-'}
                        </S.FlightDate>
                        <S.FlightTimeContainer>
                            <S.FlightTime variant={variant} $isFlown={!!segment.flown}>
                                {segment.etd.isValid ? segment.etd.time : '-'}
                            </S.FlightTime>
                            <S.AirportCode variant={variant} $isFlown={!!segment.flown}>{segment.originAirport}</S.AirportCode>
                        </S.FlightTimeContainer>
                    </S.FlightDateTime>
                    <S.FlightArrow aria-hidden="true">→</S.FlightArrow>
                    <S.FlightDateTime>
                        <S.FlightDate variant={variant}>
                            {segment.eta.isValid ? segment.eta.date : '-'}
                        </S.FlightDate>
                        <S.FlightTimeContainer>
                            <S.FlightTime variant={variant} $isFlown={!!segment.flown}>
                                {segment.eta.isValid ? segment.eta.time : '-'}
                            </S.FlightTime>
                            <S.AirportCode variant={variant} $isFlown={!!segment.flown}>{segment.destinationAirport}</S.AirportCode>
                        </S.FlightTimeContainer>
                    </S.FlightDateTime>
                </S.FlightTimes>
            </S.FlightDetailsContainer>
        </S.FlightSegment>
    );
};

export const ItinerarySegment = React.memo(ItinerarySegmentComponent, (prevProps, nextProps) => {
    return (
        prevProps.variant === nextProps.variant &&
        prevProps.segment.leg === nextProps.segment.leg &&
        prevProps.segment.flightNumber === nextProps.segment.flightNumber &&
        prevProps.segment.originAirport === nextProps.segment.originAirport &&
        prevProps.segment.destinationAirport === nextProps.segment.destinationAirport &&
        prevProps.segment.etd.date === nextProps.segment.etd.date &&
        prevProps.segment.etd.time === nextProps.segment.etd.time &&
        prevProps.segment.etd.isValid === nextProps.segment.etd.isValid &&
        prevProps.segment.eta.date === nextProps.segment.eta.date &&
        prevProps.segment.eta.time === nextProps.segment.eta.time &&
        prevProps.segment.eta.isValid === nextProps.segment.eta.isValid &&
        prevProps.segment.flown === nextProps.segment.flown
    );
});